
























































import { Vue, Component } from 'vue-property-decorator';
import {
  CreateNewGroupRequestPayload,
  CreateNewGroupResponsePayload,
  CreateNewPrimaryOrgResponse,
  UserAttributesStringInputOption
} from '@/store/modules/admin/types/group.types';
import GroupNameAndTypeForm from '@/views/AdminCreateGroup/components/GroupNameAndTypeForm.vue';
import {
  GetProtectedGroupTypesResponsePayload,
  GroupType
} from '@/store/modules/admin/types/admin.types';
import { RootState } from '@/store/store';
import { Action, State } from 'vuex-class';
import { ValidationObserver, ValidationObserverInstance } from 'vee-validate';
import {
  GroupLevelAttribute,
  GroupLevelAttributeValue
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import AdminGroupLevelAttribute from '@/views/AdminGroupSettings/components/GroupLevelAttribute.vue';
import { DialogProgrammatic, ToastProgrammatic } from 'buefy';
import delay from 'delay';
import BaseLoading from '@/components/base/BaseLoading.vue';
import { GetSignedUrlForUploadResponsePayload } from '@/store/modules/admin/types/group-level-attribute.types';
import { InsertGroupLevelAttributeValueDTO } from '@/jbi-shared/dto/jaas-group-level-attributes.dtos';
import { ApiState } from '@/store/types/general.types';
import { convertToAttributeOptionsWithValue } from '@/utils/group-level-attribute.util';
import DiscardOrganisationCreationModal from './DiscardOrganisationCreationModal.vue';

@Component({
  components: {
    ValidationObserver,
    GroupNameAndTypeForm,
    AdminGroupLevelAttribute,
    DiscardOrganisationCreationModal,
    BaseLoading
  }
})
export default class CreateNewOrganisationModal extends Vue {
  name = '';
  type: UserAttributesStringInputOption | null = null;
  agreementFiles: Array<File | GetSignedUrlForUploadResponsePayload> = [];
  activeGroupLevelAttributes: GroupLevelAttributeValue[] = [];
  groupLevelAttributeFormHasError: boolean = false;
  isGroupLevelAttributeLoading: boolean = false;
  createGroupLoading: boolean = false;
  groupCreationSuccess: boolean = false;

  @Action('admin/getGroupTypes')
  getGroupTypes!: () => void;

  @Action('admin/createNewGroup')
  createNewGroup!: (
    payload: CreateNewGroupRequestPayload
  ) => Promise<CreateNewGroupResponsePayload>;

  @Action('admin/getProtectedGroupTypes')
  getProtectedGroupTypes!: () => void;

  /* Actions and States: Group Level Attribute Values */
  @Action('admin/createGroupLevelAttributeValues')
  createGroupLevelAttributeValues!: (
    payload: InsertGroupLevelAttributeValueDTO
  ) => Promise<void>;

  @State(
    ({ admin }: RootState) => admin.apiState.createGroupLevelAttributeValues
  )
  createGroupLevelAttributeValuesApiState!: ApiState;

  @State(({ admin }: RootState) => admin.groupTypes)
  groupTypes!: GroupType[];

  @State((state: RootState) => state.admin.protectedGroupTypes)
  public protectedGroupTypes!: GetProtectedGroupTypesResponsePayload;

  get typeOptions(): UserAttributesStringInputOption[] {
    return Object.values(this.groupTypes).map((groupType: GroupType) => ({
      id: groupType.id,
      slug: groupType.name,
      name: groupType.name
    }));
  }

  get groupId() {
    return this.$route.params.groupId;
  }

  // Use explicitly for getting group data
  get protectedGroupData() {
    return this.protectedGroupTypes.find(
      (type) => type.slug === 'jtms_primary_org'
    )!;
  }

  discardChangesAndClose(): void {
    this.$buefy.modal.open({
      parent: this,
      component: DiscardOrganisationCreationModal,
      hasModalCard: true,
      trapFocus: true,
      events: {
        exit: () => {
          // Clear all active GLAs
          this.activeGroupLevelAttributes = [];
          this.closeModal();
        }
      }
    });
  }

  groupCreationFormHasError(
    isValidationObserverInvalid: boolean,
    isValidationObserverValidated: boolean
  ): boolean {
    return (
      isValidationObserverInvalid ||
      !isValidationObserverValidated ||
      this.groupLevelAttributeFormHasError
    );
  }

  handleUpdateGroupLevelAttributeValues(
    activeValues: GroupLevelAttributeValue[],
    error: boolean
  ) {
    this.activeGroupLevelAttributes = activeValues;
    this.groupLevelAttributeFormHasError = error;
  }

  async processGroupCreation() {
    const rootValidationObserver = this.$refs
      .rootValidationObserver as ValidationObserverInstance;

    if ((await rootValidationObserver.validate()) === false) {
      this.createGroupLoading = false;
      const message = !this.name?.trim()
        ? 'Name is required'
        : !this.type
        ? 'Group type is required'
        : 'Please fill in all the required fields';

      return DialogProgrammatic.alert({
        title: `Error`,
        message: `<p class="subtitle">${message}<p>`,
        type: 'is-primary'
      });
    }

    try {
      this.createGroupLoading = true;

      // Extra check to ensure protectedGroupData is loaded
      if (!this.protectedGroupData) {
        // Display error when protected group data retrieval returns null/undefined
        return DialogProgrammatic.alert({
          title: `Error`,
          message: `<p class="subtitle">Could not find matching protected group type for slug 'jtms_primary_org'<p>`,
          type: 'is-primary'
        });
      }

      const payload: CreateNewGroupRequestPayload = {
        name: this.name,
        // To be assigned in backend based on existing template attributes
        attributesSpecs: [],
        // Mapping is done here because agreements is cannot be initialized as an empty array
        agreements: this.agreementFiles.map((file) => ({
          fileUri: (file as GetSignedUrlForUploadResponsePayload).storageUri,
          fileName: (file as GetSignedUrlForUploadResponsePayload).fileName
        })),
        /**
         * Users cannot be added to primary orgs in this modal hence it is not
         * necessary to initialize the array with member data or perform members
         * data sanitization at this stage.
         */
        userAttributesArr: [],
        types: [this.type!.slug],
        // No need to notify members because members cannot be added in this UI
        notify: false,
        emailDomains: [],
        // To be assigned in backend to minimize frontend api calls
        protectedGroup: {
          isProtected: true,
          protectedGroupType: this.protectedGroupData
        }
      };

      const { id } = await this.createNewGroup(payload);
      this.createGroupLoading = false;

      const glaValuesPayload: InsertGroupLevelAttributeValueDTO = {
        groupId: id,
        attributeValues: convertToAttributeOptionsWithValue(
          this.activeGroupLevelAttributes
        )
      };

      if (this.activeGroupLevelAttributes.length) {
        this.isGroupLevelAttributeLoading = true;
        await this.createGroupLevelAttributeValues(glaValuesPayload);
        this.isGroupLevelAttributeLoading = false;
      }

      if (!this.createGroupLoading && !this.isGroupLevelAttributeLoading) {
        this.groupCreationSuccess = true;

        ToastProgrammatic.open({
          queue: true,
          type: 'is-dark',
          position: 'is-top',
          message: `Primary Organisation created`
        });

        const response: CreateNewPrimaryOrgResponse = {
          groupCreationSuccess: this.groupCreationSuccess,
          groupId: id
        };

        this.$emit('addNewPrimaryOrganisation', response);
        this.closeModal();
      }
    } catch (error: any) {
      this.groupCreationSuccess = false;
      ToastProgrammatic.open({
        queue: true,
        type: 'is-danger',
        position: 'is-top',
        message: String(error?.response?.data?.message || error),
        duration: 5000
      });
    }
  }

  closeModal() {
    this.resetFields();
    this.$emit('close');
  }

  resetFields() {
    this.name = '';
    this.type = null;
    this.activeGroupLevelAttributes = [];
  }

  async mounted() {
    this.getProtectedGroupTypes();
    this.getGroupTypes();
  }
}
